// FILE: src/components/CourseProgressTable.jsx
// A student's chapter progress as a table (now used by the admin compliance
// record). Props are unchanged: pass `chaptersProp` + `chaptersViewedProp`
// to show someone else's progress (admin), or nothing to use the signed-in
// student's own (Redux). The look lives in admin/LessonProgressTable.
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import { selectLessonsBlocked } from "../features/timeSlice";
import { fetchStudentResources } from "../features/resourceSlice";
import { EmptyState, ErrorState, LoadingState } from "./ui";
import LessonProgressTable from "./admin/LessonProgressTable";
import { mergeLessons } from "./student/lessons";
import { BLOCKED_REASON } from "./student/LessonList";

/**
 * @param {Object[]} [chaptersProp] - Chapters to show (skips the Redux fetch).
 * @param {Object[]} [chaptersViewedProp] - Their progress records (skips Redux).
 * @param {"CHAPTER"|"HOMELINK"} [resourceType="CHAPTER"] - What to fetch when no props are given.
 * @param {boolean} [viewOnly=false] - Hide the row actions.
 */
export default function CourseProgressTable({ chaptersProp, chaptersViewedProp, resourceType = "CHAPTER", viewOnly = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { chapters: reduxChapters, loading: resourcesLoading, error: resourcesError } = useSelector((state) => state.resources);
  const { profile } = useSelector((state) => state.profile);
  // Daily cap, break or ended enrolment: the server would refuse the lesson (WP-3).
  const blocked = useSelector(selectLessonsBlocked);
  const { chaptersViewed: reduxChaptersViewed } = useSelector((state) => state.progress);

  const chapters = chaptersProp || reduxChapters;
  const chaptersViewed = chaptersViewedProp || reduxChaptersViewed;

  useEffect(() => {
    if (!chaptersProp) dispatch(fetchStudentResources(resourceType));
    // Mount-only fetch by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => mergeLessons(chapters || [], chaptersViewed || [], "chapterId"), [chapters, chaptersViewed]);

  if (!chaptersProp && resourcesLoading && !rows.length) return <LoadingState variant="table" rows={4} label="Loading chapters…" />;
  if (!chaptersProp && resourcesError && !rows.length) return <ErrorState compact error={resourcesError} />;
  if (!rows.length) return <EmptyState compact title="No chapters yet" description="Chapters appear here when they're published." />;

  return (
    <>
      <LessonProgressTable
        rows={rows}
        kind="chapter"
        viewOnly={viewOnly}
        blocked={blocked}
        untimed={!!profile?.untimed /* audited admin flag, server-honoured (D8) */}
        blockedReasonId="lessons-blocked-reason-chapters"
        onOpen={(row) => navigate(`/student/resources/${row._id}?resourceType=chapter`)}
      />
      {blocked && !viewOnly && (
        <Typography id="lessons-blocked-reason-chapters" sx={{ mt: 1.5, color: "text.secondary" }} data-testid="lessons-blocked">
          {BLOCKED_REASON[blocked]}
        </Typography>
      )}
    </>
  );
}
